import React from 'react';
import styled from '@emotion/styled';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import useMediaQuery from '@mui/material/useMediaQuery';

import { PrimaryButton } from '@/components/button';
import { H3, SectionHeadingStyles } from '@/components/type';
import { BREAKPOINTS, SPACE } from '@/constants';
import { useGutterSpacing } from '@/hooks/use-gutter-spacing';
import { ContentBlockWrapper } from '@/components/content-block-wrapper';
import { urlFor } from '@/utils/sanity-image';
import { type Dereference } from '@tac/sanity-client/type-utils';
import { type HsiycSafetyFeatureShowcaseBlock } from '@tac/sanity-client/types';

const StyledH3 = styled(H3)`
  margin-bottom: ${SPACE.X1};
`;

const StyledBodyDiv = styled.div`
  ${SectionHeadingStyles};
`;

const StyledPrimaryButton = styled(PrimaryButton)`
  &.MuiButton-root {
    margin-top: ${SPACE.X2};
  }
`;

const StyledContainer = styled(Container)`
  position: relative;
`;

const StyledBox = styled(Box)`
  width: 100%;
  z-index: 0;
`;

const SafetyFeatureShowcaseBlock = ({
  backgroundImage,
  heading,
  body,
  safetyFeature,
}: Dereference<HsiycSafetyFeatureShowcaseBlock>) => {
  const lgUp = useMediaQuery(`(min-width: ${BREAKPOINTS.LG}px)`);
  const mdUp = useMediaQuery(`(min-width: ${BREAKPOINTS.MD}px)`);

  const currentSlug = safetyFeature?.slug?.current;

  const backgroundImageUrl = urlFor(backgroundImage)?.url?.();
  const safetyFeatureUri = currentSlug ? `/safety-features/${currentSlug}` : undefined;

  return (
    <ContentBlockWrapper
      useBackgroundImage={!!backgroundImageUrl}
      url={backgroundImageUrl}
      aria-label={backgroundImage?.asset?.altText}
    >
      <StyledContainer maxWidth="xl">
        <Box pt={lgUp ? SPACE.X3 : SPACE.X2} pb={lgUp ? SPACE.X3 : '48px'}>
          <Grid container spacing={useGutterSpacing()}>
            <Grid item container alignItems="center" xs={7} sm={6}>
              <StyledBox justifyContent="flex-start">
                {heading && <StyledH3 as="h2">{heading}</StyledH3>}
                {body && <StyledBodyDiv>{body}</StyledBodyDiv>}
              </StyledBox>
              {safetyFeatureUri && (
                <StyledPrimaryButton eventText="See more" fullWidth={!mdUp} href={safetyFeatureUri}>
                  See more
                </StyledPrimaryButton>
              )}
            </Grid>
          </Grid>
        </Box>
      </StyledContainer>
    </ContentBlockWrapper>
  );
};

export default SafetyFeatureShowcaseBlock;
